import React, { useContext, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormHelperText } from '@material-ui/core';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { UserProviderContext } from 'providers/UserProvider';
import { userHasRole } from 'services/Authorization';
import strings from 'strings';
import TextBox from 'UI/components/atoms/TextBox';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPTextEditor from 'UI/components/molecules/FPTextEditor';
import { tinymceConfig } from 'UI/constants/config';
import { ActivityLogTypes, DateFormats } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import { idOptionSelected, REQUIRED_VALIDATION, toLocalTime } from 'UI/utils';

import { FORM_FIELDS_MAP } from './ActivityForm.constants';
import { useStyles } from './ActivityForm.styles';

const { form: STRINGS } = strings.inventoryProfiles.sections.tabs.activityNote;

export const ActivityForm = ({ activityData, shouldHideOutcomeField = false }) => {
  const classes = useStyles();
  const { errors, register, unregister, watch, setValue, getValues } = useFormContext();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const [user] = useContext(UserProviderContext);
  const isOperationsUser = userHasRole(Roles.Operations, user);

  const localTime = toLocalTime(activityData.createdAt);
  const formattedDate = localTime && localTime.format(DateFormats.SimpleDateTime);
  const isExistingActivity = !!activityData.createdAt;
  const isOwner = user.id === activityData.userId;
  const canEditActivityFields = isExistingActivity ? isOperationsUser : true;
  const canEditOutcomeFields = isExistingActivity ? isOwner || isOperationsUser : true;

  const areOutcomeFieldsEnabled =
    checkIfFeatureFlagEnabled(FeatureFlags.ActivityOutcomeFieldsInForm) &&
    activityData.entity !== EntityType.Joborder;

  const editorRef = useRef(null);

  const watchers = watch([
    FORM_FIELDS_MAP.ActivityType.key,
    FORM_FIELDS_MAP.ActivityBody.key,
    FORM_FIELDS_MAP.ActivityOutcome.key,
    FORM_FIELDS_MAP.ActivityOutcomeComments.key
  ]);
  const { activityType, activityBody, activityOutcome, activityOutcomeComments } = watchers;
  const activityTypeId = activityType?.id;

  const requiresIframeViewer = [
    ActivityLogTypes.Email,
    ActivityLogTypes.BulkEmail,
    ActivityLogTypes.InMailReply
  ].includes(activityTypeId);

  const initEditor = (e, editor) => {
    editorRef.current = editor;
  };

  const handleEditorChange = editorValue =>
    setValue(FORM_FIELDS_MAP.ActivityBody.key, editorValue, true);

  const handleCommentsChange = (fieldName, newValue) => {
    setValue(fieldName, newValue);
  };

  const handleSelectChange = (name, value) => setValue(name, value, true);

  useEffect(() => {
    register(FORM_FIELDS_MAP.ActivityType.key, REQUIRED_VALIDATION);
    register(FORM_FIELDS_MAP.ActivityBody.key, { required: !activityData.isCreatedBySystem });

    if (areOutcomeFieldsEnabled) {
      register(FORM_FIELDS_MAP.ActivityOutcome.key, {
        validate: value => {
          const comments = getValues(FORM_FIELDS_MAP.ActivityOutcomeComments.key);
          if (comments && (!value || !value.typeId)) {
            return STRINGS.validation.activityOutcome;
          }
          return true;
        }
      });

      register(FORM_FIELDS_MAP.ActivityOutcomeComments.key, {
        validate: value => {
          const outcome = getValues(FORM_FIELDS_MAP.ActivityOutcome.key);
          if (value && (!outcome || !outcome.typeId)) {
            return STRINGS.validation.activityOutcomeComments;
          }
          return true;
        }
      });
    }

    return () => {
      unregister(FORM_FIELDS_MAP.ActivityType.key);
      unregister(FORM_FIELDS_MAP.ActivityBody.key);
      if (areOutcomeFieldsEnabled) {
        unregister(FORM_FIELDS_MAP.ActivityOutcome.key);
        unregister(FORM_FIELDS_MAP.ActivityOutcomeComments.key);
      }
    };
  }, [register, unregister, areOutcomeFieldsEnabled, getValues, activityData.isCreatedBySystem]);

  const renderActivityPreview = () => {
    if (activityTypeId === ActivityLogTypes.BulkEmail) {
      return (
        <div
          className={classes.emailBodyRenderer}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: activityData?.contextualData?.bulkEmailPreview ?? activityBody
          }}
        />
      );
    }

    if (
      activityTypeId === ActivityLogTypes.Email ||
      activityTypeId === ActivityLogTypes.InMailReply
    ) {
      return (
        <iframe
          data-testid="email-frame"
          title="Email preview"
          className={classes.emailBodyRenderer}
          srcDoc={activityData?.contextualData?.mailBody ?? activityBody}
        />
      );
    }

    return null;
  };

  return (
    <div className={classes.formContainer}>
      <AutocompleteSelect
        name={FORM_FIELDS_MAP.ActivityType.key}
        selectedValue={activityType}
        placeholder="Type *"
        error={!!errors[FORM_FIELDS_MAP.ActivityType.key]}
        errorText={errors[FORM_FIELDS_MAP.ActivityType.key]?.message}
        url={Endpoints.ActivityTypes}
        getOptionLabel={option => option.title}
        getOptionSelected={idOptionSelected}
        onSelect={handleSelectChange}
        disabled={activityData.isCreatedBySystem || !canEditActivityFields}
      />
      <When
        condition={isExistingActivity && activityData.isCreatedBySystem && requiresIframeViewer}
      >
        {renderActivityPreview}
      </When>
      <When
        condition={
          activityData.isCreatedBySystem ? !requiresIframeViewer : !activityData.isCreatedBySystem
        }
      >
        <FPTextEditor
          onInit={initEditor}
          config={tinymceConfig}
          value={activityBody}
          onChange={handleEditorChange}
          disabled={activityData.isCreatedBySystem || !canEditActivityFields}
          error={!!errors[FORM_FIELDS_MAP.ActivityBody.key]}
          errorText={errors[FORM_FIELDS_MAP.ActivityBody.key]?.message}
        />
      </When>
      <When condition={areOutcomeFieldsEnabled && !shouldHideOutcomeField}>
        <AutocompleteSelect
          name={FORM_FIELDS_MAP.ActivityOutcome.key}
          selectedValue={activityOutcome}
          placeholder="Outcome"
          url={`${Endpoints.ActivityOutcomes}?entityType=${activityData?.entity}`}
          getOptionLabel={option => option.typeTitle}
          groupBy={option => option.categoryTitle}
          getOptionSelected={(option, value) => option.typeId === value?.typeId}
          onSelect={handleSelectChange}
          disabled={!canEditOutcomeFields}
          error={!!errors[FORM_FIELDS_MAP.ActivityOutcome.key]}
          errorText={errors[FORM_FIELDS_MAP.ActivityOutcome.key]?.message}
        />
        <TextBox
          label="Comments"
          name={FORM_FIELDS_MAP.ActivityOutcomeComments.key}
          onChange={handleCommentsChange}
          value={activityOutcomeComments || ''}
          disabled={!canEditOutcomeFields}
        />
      </When>
      <When condition={activityData.createdAt}>
        <FormHelperText className={classes.helper}>
          {STRINGS.label.createdBy}{' '}
          <b>
            {activityData.userName} - {activityData.userEmail}
          </b>
          {activityData.createdAt && (
            <>
              {' '}
              {STRINGS.label.createdDate}: <b>{formattedDate}</b>
            </>
          )}
        </FormHelperText>
      </When>
    </div>
  );
};
